import { useEffect, useState } from 'react';

import { Button, Col, Row } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useUpdateEffect } from 'react-use';
import { DateTime } from 'luxon';

import { useAppDispatch, useAppSelector } from 'hooks';
import { ModalV2, SectionTitle } from 'components/UI';
import { DropdownGroup } from 'components/Forms';
import { Error, Loading } from 'components/Fetch';
import { useAxios } from 'configs/requests';
import { AccessRoles } from 'constants/enums';
import { isPatientArchived, selectPatientBaselines } from 'store/patient/reducer';
import { getBaselines } from 'store/patient/service';
import { getUserRoles } from 'store/oauth/reducer';
import {
  PRESSURE_BASELINE_CLASS,
  formatBaselineRange,
  getBaselineUnitLabel,
  getBaselineParts,
  sortBaselines
} from 'utils/baselines';

import AddEditBaseline from './AddEditBaseline';

interface IBaselineProps {
  mode: IEditCarePlanMode;
  carePlan?: ICarePlan;
  carePlanTemplate?: ICarePlanTemplate;
}

const Baseline = ({ mode, carePlan, carePlanTemplate }: IBaselineProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const locale = useAppSelector((state) => state.intl.locale);
  const patient = useAppSelector(({ patient }) => patient?.data);
  const patientBaselines = useAppSelector(selectPatientBaselines);
  const patientBaselinesUid = useAppSelector(({ patient }) => patient.baselines.uid);
  const patientLoading = useAppSelector(({ patient }) => patient.baselines.loading);
  const patientError = useAppSelector(({ patient }) => patient.baselines.error);
  const _isPatientArchived = useAppSelector(isPatientArchived) && mode === 'carePlan';
  const userRoles = useAppSelector((state) => getUserRoles(state)) || [];
  const isClinician = userRoles.includes(AccessRoles.ADMIN);
  const isTemplateGlobal = carePlanTemplate?.template_type === 'GLOBAL';
  const isReadOnly = mode === 'template' ? isTemplateGlobal : !isClinician;
  const canAdd = mode === 'carePlan' ? !!patient?.uid : !!carePlanTemplate?.template_id;
  const [addEditModal, setAddEditModal] = useState(false);
  const [selected, setSelected] = useState<IBaselineItem>();
  const [dialogError, setDialogError] = useState(false);
  const [toDelete, setToDelete] = useState<IBaselineItem>();
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const templateId = carePlanTemplate?.template_id;
  const templateBaselinesUrl = `/clinician-gateway/care-plan-templates/${templateId}/baselines`;

  const [
    { data: templateData, loading: templateLoading, error: templateError },
    getTemplateBaselines
  ] = useAxios<{ baselines: IBaselineItem[] }>(
    { url: templateBaselinesUrl, method: 'GET' },
    { manual: true, useCache: false }
  );

  const [{ loading: saveLoading }, save] = useAxios({ method: 'POST' }, { manual: true });

  const refresh = () => {
    if (mode === 'carePlan') {
      if (patient?.uid) dispatch(getBaselines(patient.uid));
      return;
    }
    getTemplateBaselines().catch(() => undefined);
  };

  const submit = (request: Promise<unknown>) => {
    setDialogError(false);
    request
      .then(() => {
        setAddEditModal(false);
        refresh();
      })
      .catch(() => setDialogError(true));
  };

  const handleSave = (values: IBaselineFormValues) => {
    const { class: className, ...body } = values;
    if (mode === 'carePlan') {
      submit(
        save({
          url: `/clinician-gateway/patient/${patient?.uid}/baselines/${className}`,
          method: 'PUT',
          data: body
        })
      );
      return;
    }
    submit(
      save({
        url: `${templateBaselinesUrl}/${className}`,
        method: 'PUT',
        data: body
      })
    );
  };

  const handleRemove = () => {
    const baseline = toDelete;
    if (!baseline) return;
    setDeleteError(false);
    const request = save({
      url:
        mode === 'carePlan'
          ? `/clinician-gateway/patient/${patient?.uid}/baselines/${baseline.class}`
          : `${templateBaselinesUrl}/${baseline.class}`,
      method: 'DELETE'
    });
    request
      .then(() => {
        setDeleteModal(false);
        refresh();
      })
      .catch(() => setDeleteError(true));
  };

  const handleEdit = (baseline: IBaselineItem) => {
    setSelected(baseline);
    setAddEditModal(true);
  };

  const renderRange = (baseline: IBaselineItem) => {
    const unit = getBaselineUnitLabel(baseline.class, baseline.unit, intl);
    const parts = getBaselineParts(baseline);
    const isPressure = baseline.class === PRESSURE_BASELINE_CLASS;
    return parts.map(({ className, range }, index) => (
      <div key={className} className="d-flex align-items-baseline">
        {isPressure && (
          <span className="baseline-card-label text-gray-550">
            {intl.formatMessage({
              id: className === 'systolic' ? 'carePlanBaselineSysKey' : 'carePlanBaselineDiaKey'
            })}
          </span>
        )}
        <span className="baseline-card-value text-blue-dark">
          {formatBaselineRange(range, className, baseline.unit)}
        </span>
        {index === parts.length - 1 && (
          <span className="baseline-card-unit text-gray-550 ms-2">{unit}</span>
        )}
      </div>
    ));
  };

  const formatMeta = (baseline: IBaselineItem) => {
    const date = baseline.effectiveDate
      ? DateTime.fromISO(baseline.effectiveDate).setLocale(locale).toLocaleString(DateTime.DATE_MED)
      : '';
    const author = baseline.setBy
      ? baseline.setBy.isSystem
        ? intl.formatMessage({ id: 'carePlanBaselineSystemAuthorKey' })
        : baseline.setBy.displayName
      : '';
    return [date, author].filter(Boolean).join(' · ');
  };

  useUpdateEffect(() => {
    setDeleteError(false);
  }, [toDelete]);

  useUpdateEffect(() => {
    if (!addEditModal) setSelected(undefined);
    setDialogError(false);
  }, [addEditModal]);

  useEffect(() => {
    if (mode === 'template' && templateId) getTemplateBaselines().catch(() => undefined);
  }, [mode, templateId, getTemplateBaselines]);

  useEffect(() => {
    if (mode === 'carePlan' && patient?.uid) dispatch(getBaselines(patient.uid));
  }, [dispatch, mode, patient?.uid, carePlan]);

  const baselines: IBaselineItem[] =
    mode === 'carePlan' ? patientBaselines : templateData?.baselines || [];
  const getLoading =
    mode === 'carePlan'
      ? patientLoading || (!!patient?.uid && patientBaselinesUid !== patient.uid && !patientError)
      : templateLoading || (!!templateId && !templateData && !templateError);
  const getError = mode === 'carePlan' ? patientError : !!templateError;
  const sortedBaselines = sortBaselines(baselines);

  return (
    <div className="mb-5" data-test="careplan-baseline-section">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-baseline flex-wrap">
          <SectionTitle
            dataTestId="careplan-baseline-title"
            label={intl.formatMessage({ id: 'carePlanBaselineKey' })}
            className="mb-0 me-3"
          />
          <span className="small text-gray-550" data-test="careplan-baseline-subtitle">
            {intl.formatMessage({ id: 'carePlanBaselineSubtitleKey' })}
          </span>
        </div>
        <AddEditBaseline
          show={addEditModal}
          setModal={setAddEditModal}
          mode={mode}
          baseline={selected}
          ignoreClasses={baselines.map((x) => x.class)}
          loading={saveLoading}
          error={dialogError}
          onSave={handleSave}
        />
        {!isReadOnly && (
          <Button
            data-test="careplan-baseline-btn"
            variant="link"
            size="sm"
            onClick={() => setAddEditModal(true)}
            disabled={_isPatientArchived || !canAdd}
          >
            + {intl.formatMessage({ id: 'carePlanBaselineKey' })}
          </Button>
        )}
      </div>
      {getLoading && <Loading />}
      {!getLoading && getError && <Error retry={refresh} />}
      <ModalV2
        data-test="delete-baseline-modal"
        className="baseline-modal"
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onExited={() => setToDelete(undefined)}
      >
        <ModalV2.Header closeButton className="align-items-center">
          <ModalV2.Title>{intl.formatMessage({ id: 'carePlanBaselineDeleteKey' })}</ModalV2.Title>
        </ModalV2.Header>
        <ModalV2.Body>
          {toDelete && (
            <div className="baseline-card p-3">
              <div className="font-weight-bolder small mb-2">
                {intl.formatMessage({ id: toDelete.class })}
              </div>
              {renderRange(toDelete)}
              {mode === 'carePlan' && (
                <div className="baseline-card-footer mt-3 pt-2">
                  <small className="text-gray-550">{formatMeta(toDelete)}</small>
                </div>
              )}
            </div>
          )}
          {saveLoading && <Loading className="mt-4" />}
          {deleteError && <Error className="mt-4" retry={handleRemove} dismissible={false} />}
        </ModalV2.Body>
        <ModalV2.Footer>
          <Button
            data-test="cancel-delete-baseline-btn"
            variant="outline-secondary"
            className="baseline-modal-cancel"
            onClick={() => setDeleteModal(false)}
          >
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>
          <Button
            data-test="confirm-delete-baseline-btn"
            variant="danger"
            className="baseline-modal-delete"
            onClick={handleRemove}
            disabled={saveLoading}
          >
            {intl.formatMessage({ id: 'deleteKey' })}
          </Button>
        </ModalV2.Footer>
      </ModalV2>
      {sortedBaselines.length > 0 ? (
        <Row>
          {sortedBaselines.map((baseline) => (
            <Col
              data-test={`baseline-${baseline.class}`}
              xl={4}
              md={6}
              xs={12}
              key={baseline.class}
              className="mb-3"
            >
              <div className="baseline-card p-3 h-100" data-test="baseline-block">
                <div className="d-flex align-items-start justify-content-between mb-2">
                  <span className="font-weight-bolder small">
                    {intl.formatMessage({ id: baseline.class })}
                  </span>
                  {!isReadOnly && (
                    <DropdownGroup
                      dataTestId={`baseline-${baseline.class}`}
                      onShowEditModal={() => handleEdit(baseline)}
                      onShowDeleteModal={() => {
                        setToDelete(baseline);
                        setDeleteModal(true);
                      }}
                      disabled={_isPatientArchived || saveLoading}
                    />
                  )}
                </div>
                <div data-test={`baseline-${baseline.class}-range`}>{renderRange(baseline)}</div>
                {mode === 'carePlan' && (
                  <div className="baseline-card-footer mt-3 pt-2">
                    <small className="text-gray-550" data-test={`baseline-${baseline.class}-meta`}>
                      {formatMeta(baseline)}
                    </small>
                  </div>
                )}
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        !getLoading &&
        !getError && (
          <div
            data-test="careplan-baseline-empty"
            className="bg-light text-center p-3 font-weight-bolder"
          >
            {intl.formatMessage({ id: 'carePlanBaselineEmptyKey' })}
          </div>
        )
      )}
    </div>
  );
};

export default Baseline;
